import { Component, inject, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { Popover } from "@openng/optimus-ui/popover";
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { SmTimer } from 'src/modules/sm-base/models/sm-timer.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { MainTabItem } from '../../models/main-tab-item.model';
import { CalendarEventKind } from '../../netgen/CalendarEventKind';
import { EventReminderInfo } from '../../netgen/EventReminderInfo';
import { Project } from '../../netgen/Project';
import { ProjectPersonalSummary } from '../../netgen/ProjectPersonalSummary';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { CalendarEventToolTipComponent } from '../calendar-event-tool-tip/calendar-event-tool-tip.component';
import { PlanningViewCompanyComponent } from '../planning-view-company/planning-view-company.component';
import { PlanningViewDocumentsComponent } from '../planning-view-documents/planning-view-documents.component';
import { PlanningViewMyWorkspaceComponent } from '../planning-view-my-workspace/planning-view-my-workspace.component';
import { PlanningViewProjectOverviewComponent } from '../planning-view-project-overview/planning-view-project-overview.component';
import { PlanningViewProjects1Component } from '../planning-view-projects1/planning-view-projects1.component';
import { PlanningViewProjects2Component } from '../planning-view-projects2/planning-view-projects2.component';

@Component({
    selector: 'app-project-planning-main',
    templateUrl: './project-planning-main.component.html',
    styleUrl: './project-planning-main.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectPlanningMainComponent extends ComponentView {

    static instance: ProjectPlanningMainComponent;

    _CalendarEventKind = CalendarEventKind;
    _CalendarEventToolTipComponent = CalendarEventToolTipComponent;

    service = inject(ProjectPlanningService);

    neededParams: ObjectOfSimpleDataTypes = {
        view: "string?",
        selectedItem: "string?"
    };
    useQueryParams = true;

    view: string;
    selectedItem: string;

    overdueCount = 0;
    todayTasksCount = 0;
    todayAppointmentsCount = 0;

    tabs: MainTabItem[] = [
        { id: 'my-workspace', title: this.app.t("ProjectPlanning.myWorkspace"), component: PlanningViewMyWorkspaceComponent },
        { id: 'company', title: this.app.t("ProjectPlanning.company"), component: PlanningViewCompanyComponent },
        { id: 'documents', title: this.app.t("ProjectPlanning.documents"), component: PlanningViewDocumentsComponent },
        { id: 'project-overview', title: this.app.t("ProjectPlanning.projectOverview"), component: PlanningViewProjectOverviewComponent },
        { id: 'projects1', title: this.app.t("ProjectPlanning.projectView1"), component: PlanningViewProjects1Component },
        { id: 'projects2', title: this.app.t("ProjectPlanning.projectView2"), component: PlanningViewProjects2Component }
    ];

    static reminderEventIdsDisplayed = new Set<number>();

    activeTabIndex = this.tabs[0].id;
    summary = new ProjectPersonalSummary();

    @ViewChild("planningViewMyWorkspace")
    planningViewMyWorkspace: PlanningViewMyWorkspaceComponent;
    @ViewChild("planningViewCompany")
    planningViewCompany: PlanningViewCompanyComponent;
    @ViewChild("planningViewProjects1")
    planningViewProjects1: PlanningViewProjects1Component;
    @ViewChild("planningViewProjects2")
    planningViewProjects2: PlanningViewProjects2Component;
    @ViewChild("globalSearchPopover")
    globalSearchPopover: Popover;

    globalSearchEventId = "";

    constructor() {
        super();
        ProjectPlanningMainComponent.instance = this;
    }

    async initParams(): Promise<boolean> {      
        this.app.updateNavigation(this.app.t("ProjectPlanning.moduleTitle"), [{ label: this.app.t("ProjectPlanning.moduleTitle"), routerLink: ["/project-planning", "home"] }]);

        this.addTimer(new SmTimer("getPersonalSummary", 0, 60 * 1_000, this.updatePersonalSummary.bind(this)));

        //Sicherstellen, dass relevante Daten geladen sind
        await this.service.getUsers();
        await this.service.getCalendars();
        await this.updatePersonalSummary();

        if (!Utils.isNoe(this.view)) {
            this.activeTabIndex = this.view;
            this.changeTab();
        }

        return true;
    }

    changeTab(): void {
        if (this.activeTabIndex == this.tabs[1].id) {
            GuiUtils.angularTimerWithRetry(() => this.planningViewCompany.setWasOpened(), () => this.activeTabIndex == this.tabs[1].id && this.planningViewCompany?.inited && this.planningViewCompany.fc != null, 100, 100);
        }
        else if (this.activeTabIndex == this.tabs[5].id) {
            GuiUtils.angularTimerWithRetry(() => this.planningViewProjects2.setWasOpened(), () => this.activeTabIndex == this.tabs[5].id && this.planningViewProjects2?.inited && this.planningViewProjects2.fc != null, 100, 100);
        }
        //TODO await this.app.navigateChangeParams({ view: this.activeTabIndex });
    }

    openProjectInView1(project: Project): void {
        let tabId = this.tabs[4].id;
        this.activeTabIndex = tabId;
        GuiUtils.angularTimerWithRetry(() => this.planningViewProjects1.setProject(project), () => this.activeTabIndex == tabId && this.planningViewProjects1?.inited, 100, 100);
    }

    async searchCalendarEvent(): Promise<void> {
        let eventId = Utils.toNumber(this.globalSearchEventId, false, false, 0);
        if (eventId <= 0) {
            return;
        }

        let event = await this.service.getEventFull(eventId);
        if (event == null) {
            await this.app.dialog.info(this.app.t("ProjectPlanning.globalSearchEventNotFound", { eventId }), this.app.t("ProjectPlanning.globalSearch"));
            return;
        }

        this.globalSearchPopover.hide();
        if (event.kind != CalendarEventKind.appointment) {
            let tabId = this.tabs[4].id;
            this.activeTabIndex = tabId;
            GuiUtils.angularTimerWithRetry(() => this.planningViewProjects1.showEvent(event), () => this.activeTabIndex == tabId && this.planningViewProjects1?.inited, 100, 100);
        }
        else {
            let tabId = this.tabs[1].id;
            this.activeTabIndex = tabId;
            GuiUtils.angularTimerWithRetry(() => this.planningViewCompany.showEvent(event), () => this.activeTabIndex == tabId && this.planningViewCompany?.inited && this.planningViewCompany.fc != null, 100, 100);
        }
    }

    async updatePersonalSummary(): Promise<void> {        
        this.summary = await this.service.getPersonalSummary(true);
        for (let r of this.summary.reminder) {
            r.event = await this.service.getEventFull(r.eventId);
        }
    }

    async reminderClick(info: EventReminderInfo): Promise<void> {
        let event = await this.service.getEventFull(info.eventId);
        await this.service.editEvent(event, () => {});
    }

    async reminderDone(eventId: number): Promise<void> {
        await RestEndpoint.main().put().query({ eventId }).run("api/projectplanning/calendarEvent/reminderDone").getText();   
        this.summary.reminder = this.summary.reminder.filter(r => r.eventId != eventId);     
    }

}
